import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  message,
  Space,
  Input,
  Tag,
  Typography,
  Card,
  Empty,
  Spin,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../api";

const { Title } = Typography;


const PreConList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/member/geteventpreconfirm");
      const fetchedData = res.data.data || res.data || [];
      const processedData = Array.isArray(fetchedData) ? fetchedData : [];
      setData(processedData);
      setFilteredData(processedData);
      message.success("Pre-confirmation records loaded successfully");
    } catch (err) {
      console.error("Fetch Error:", err);
      message.error("Failed to load pre-confirmations");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/member/deleteeventpreconfirm/${id}`);
      message.success("Record deleted successfully");
      fetchData();
    } catch (err) {
      console.error("Delete Error:", err);
      message.error("Failed to delete record");
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredData(data);
      return;
    }

    const lowerValue = value.toLowerCase();
    const filtered = data.filter((item) => {
      const eventName = item.eventid?.eventName || "";
      const members = Array.isArray(item.memberid)
        ? item.memberid
            .filter((m) => m) // Keep all available member data
            .map((m) => m.Name || "")
            .join(" ")
        : "";
      const nonMembers = Array.isArray(item.nonmember)
        ? item.nonmember
            .map((n) => `${n.name || ""} ${n.mobilenumber || ""} ${n.address || ""}`)
            .join(" ")
        : "";

      return (
        eventName.toLowerCase().includes(lowerValue) ||
        members.toLowerCase().includes(lowerValue) ||
        nonMembers.toLowerCase().includes(lowerValue)
      );
    });
    setFilteredData(filtered);
  };

  const showFullList = (title, items) => {
    setModalTitle(title);
    setModalContent(items);
    setModalVisible(true);
  };

  const columns = [
    {
      title: "Event",
      dataIndex: "eventid",
      key: "event",
      render: (eventid) => (
        <div>
          <div style={{ fontWeight: "500" }}>{eventid?.eventName || "N/A"}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {eventid?.eventDate
              ? new Date(eventid.eventDate).toLocaleDateString("en-IN")
              : "N/A"}
          </div>
        </div>
      ),
      sorter: (a, b) =>
        (a.eventid?.eventName || "").localeCompare(b.eventid?.eventName || ""),
    },
    {
      title: "Attendance",
      key: "attendees",
      responsive: ["md"],
      render: (_, record) => {
        const memberCount = Array.isArray(record.memberid)
          ? record.memberid.filter((m) => m).length
          : 0;
        const guestCount = Array.isArray(record.nonmember)
          ? record.nonmember.length
          : 0;
        const total = memberCount + guestCount;

        return (
          <Space direction="vertical" size={4}>
            <Tag color="blue">{memberCount} Members</Tag>
            <Tag color="green">{guestCount} Guests</Tag>
            <Tag color="purple">Total: {total}</Tag>
          </Space>
        );
      },
    },
    {
      title: "Members",
      dataIndex: "memberid",
      key: "memberid",
      responsive: ["lg"],
      render: (members, record) => {
        if (!Array.isArray(members)) return "N/A";

        const activeMembers = Array.isArray(members) ? members.filter((m) => m) : [];
        const displayMembers = activeMembers.slice(0, 5);
        const hasMore = activeMembers.length > 5;

        return (
          <Space wrap>
            {displayMembers.map((m, i) => (
              <Tag key={i} color="blue">
                {m.Name || "Unknown"}
              </Tag>
            ))}
            {activeMembers.length === 0 && <span style={{ color: "#999" }}>None</span>}
            {hasMore && (
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() =>
                  showFullList(
                    `All Members - ${record.eventid?.eventName || "Event"}`,
                    activeMembers.map((m) => m.Name || "Unknown")
                  )
                }
              >
                +{activeMembers.length - 5} more
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: "Guests",
      dataIndex: "nonmember",
      key: "nonmember",
      responsive: ["xl"],
      render: (nonmembers, record) => {
        if (!Array.isArray(nonmembers)) return "N/A";

        const displayGuests = nonmembers.slice(0, 5);
        const hasMore = nonmembers.length > 5;

        return (
          <Space wrap>
            {displayGuests.map((n, i) => (
              <Tag key={i} color="green">
                {n.name} ({n.mobilenumber || "N/A"})
              </Tag>
            ))}
            {nonmembers.length === 0 && <span style={{ color: "#999" }}>None</span>}
            {hasMore && (
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() =>
                  showFullList(
                    `All Guests - ${record.eventid?.eventName || "Event"}`,
                    nonmembers.map((n) => `${n.name} (${n.mobilenumber || "N/A"})`)
                  )
                }
              >
                +{nonmembers.length - 5} more
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record._id)}
          size="small"
        >
          Delete
        </Button>
      ),
    },
  ];

  // Mobile Card View
  const mobileCards = filteredData.map((record) => {
    const activeMembers = Array.isArray(record.memberid)
      ? record.memberid.filter((m) => !m.isDeleted)
      : [];
    const guests = Array.isArray(record.nonmember) ? record.nonmember : [];

    return (
      <Card key={record._id} style={{ marginBottom: 16 }} size="small">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div style={{ flex: 1 }}>
            <Title level={5} style={{ margin: "0 0 8px 0" }}>
              {record.eventid?.eventName || "N/A"}
            </Title>
            <div style={{ color: "#666", fontSize: "14px", marginBottom: "12px" }}>
              📅{" "}
              {record.eventid?.eventDate
                ? new Date(record.eventid.eventDate).toLocaleDateString("en-IN")
                : "N/A"}
            </div>

            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <div>
                <strong>Members ({activeMembers.length}):</strong>{" "}
                {activeMembers.length > 0 ? (
                  <>
                    {activeMembers.slice(0, 5).map((m) => m.Name).join(", ")}
                    {activeMembers.length > 5 && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          showFullList(
                            `All Members - ${record.eventid?.eventName}`,
                            activeMembers.map((m) => m.Name)
                          )
                        }
                      >
                        ... +{activeMembers.length - 5} more
                      </Button>
                    )}
                  </>
                ) : (
                  "None"
                )}
              </div>
              <div>
                <strong>Guests ({guests.length}):</strong>{" "}
                {guests.length > 0 ? (
                  <>
                    {guests
                      .slice(0, 5)
                      .map((n) => `${n.name} (${n.mobilenumber || "N/A"})`)
                      .join(", ")}
                    {guests.length > 5 && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          showFullList(
                            `All Guests - ${record.eventid?.eventName}`,
                            guests.map((n) => `${n.name} (${n.mobilenumber || "N/A"})`)
                          )
                        }
                      >
                        ... +{guests.length - 5} more
                      </Button>
                    )}
                  </>
                ) : (
                  "None"
                )}
              </div>
            </Space>
          </div>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          />
        </div>
      </Card>
    );
  });

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      <Title level={2} style={{ marginBottom: "24px" }}>
        Event Pre-Confirmation Records
      </Title>

      <div style={{ marginBottom: "24px", display: "flex", flexWrap: "wrap", gap: "12px" }}>
        <Input.Search
          placeholder="Search by event, member, or guest..."
          allowClear
          enterButton="Search"
          size="large"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          style={{ flex: "1 1 300px", maxWidth: "400px" }}
        />

        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/dashboard/pre-con")}
            size="large"
          >
            New Pre-Confirmation
          </Button>
        </Space>
      </div>

      {/* Desktop Table */}
      <div className="desktop-view">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          loading={{ spinning: loading, indicator: <Spin size="large" /> }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          }}
          scroll={{ x: 800 }}
          locale={{ emptyText: <Empty description="No pre-confirmation records found" /> }}
        />
      </div>

      {/* Mobile Card View */}
      <div className="mobile-view" style={{ display: "none" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : filteredData.length === 0 ? (
          <Empty description="No pre-confirmation records found" />
        ) : (
          mobileCards
        )}
      </div>

      {/* Full List Modal */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {modalContent.length > 0 ? (
            modalContent.map((item, index) => (
              <Tag key={index} color="default" style={{ marginBottom: "8px" }}>
                {item}
              </Tag>
            ))
          ) : (
            <p>No data available</p>
          )}
        </Space>
      </Modal>

      {/* Responsive Switch */}
      <style jsx>{`
        @media (max-width: 768px) {
          .desktop-view {
            display: none !important;
          }
          .mobile-view {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PreConList;